<template>
    <ag-grid-vue
        v-if="invoiceTab !== INVOICE_TAB.DRAFT"
        class="w-full h-full min-w-[830px] ag-theme-balham overflow-auto"
        :row-data="filteredInvoices"
        :column-defs="columns"
        :grid-options="gridOptions"
        :theme="agTheme"
    /> <!-- TODO: fix height being outside of screen -->
    <invoice-draft-list v-else />
</template>

<script setup lang="ts">

import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, ValueGetterParams} from 'ag-grid-community'
import {globalDefaultColDef} from '@/agUtils'
import {computed, inject, onMounted, onUnmounted, ref, Ref, shallowRef, watch} from 'vue'
import {InvoiceBalance} from '@/invoice/InvoiceBalance'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SWITCHED} from '@/types/events'
import {getInvoiceBalancesWithDueDate} from '@/invoice/invoice.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import {INVOICE_TAB} from '@/invoice/types/InvoiceTab'
import InvoiceLinkCell from '@/invoice/InvoiceLinkCell.vue'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import InvoiceSaccCellRenderer from '@/invoice/InvoiceSaccCellRenderer.vue'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import DueDaysCellRenderer from '@/invoice/DueDaysCellRenderer.vue'
import InvoiceDebtCollectionCell from '@/invoice/InvoiceDebtCollectionCell.vue'
import {ShowDebtCollection} from '@/tenants/org.features'
import InvoiceDraftList from '@/invoice/InvoiceCreator/InvoiceDraftList.vue'

const props = defineProps({
    invoiceTab: {
        type: Object as () => INVOICE_TAB,
        required: true
    },
})

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const gridApi = shallowRef<GridApi>()

const allData = ref<InvoiceBalance[]>()
const selectedInvoice = defineModel<InvoiceBalance>('selectedInvoice')

const filteredInvoices = computed(() => {
    switch (props.invoiceTab) {
        case INVOICE_TAB.DEBT_COLLECTION:
            return allData.value?.filter(invoice => invoice.debtCollectionStatus !== 'Ikke sendt' && invoice.balance > 0)
        case INVOICE_TAB.PAID:
            return allData.value?.filter(invoice => invoice.balance === 0)
        case INVOICE_TAB.UNPAID:
            return allData.value?.filter(invoice => invoice.balance > 0)
        case INVOICE_TAB.ALL:
            return allData.value
        default:
            return allData.value
    }
})

const {onEvent, cleanUp} = useEmitter()

const handleInvoiceSelected = (invoice: InvoiceBalance) => {
    selectedInvoice.value = invoice
}

const columns: ColDef[] = [
    {
        headerName: 'Fakturanummer',
        field: 'invoiceNo',
        flex: 1,
        minWidth: 130,
        maxWidth: 200,
        cellRenderer: InvoiceLinkCell,
        onCellClicked: (event) => handleInvoiceSelected(event.data),
        cellRendererParams: () => ({
            previewMode: true
        }),
        sortable: true
    },
    {
        headerName: 'Dato',
        field: 'date',
        width: 110,
        cellRenderer: (params: ValueGetterParams) => {
            return formattedDate(params.data.date)
        },
        sortable: true
    },
    {
        headerName: 'Forfallsdato',
        field: 'dueDate',
        width: 117,
        cellRenderer: (params: ValueGetterParams) => {
            return formattedDate(params.data.dueDate)
        },
        sortable: true
    },
    {
        headerName: 'Kunde',
        cellRenderer: InvoiceSaccCellRenderer,
        flex: 1,
        minWidth: 90,
        valueGetter: (params: ValueGetterParams) => {
            const subAccount = findSubAccountByUuid(params.data.subAccountUuid)
            return subAccount ? subAccount.name : ''
        },
        sortable: true
    },
    {
        headerName: 'Type',
        field: 'invoiceType',
        width: 80,
        hide: true,
        sortable: true
    },
    {
        headerName: 'Beløp',
        valueGetter: (params: ValueGetterParams) => {
            return params.data.amount
        },
        minWidth: 90,
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (node: ICellRendererParams) => formatCurrency(node.value, 2),
        sortable: true
    },
    {
        headerName: 'Restbeløp',
        field: 'balance',
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (node: ICellRendererParams) => formatCurrency(node.value, 2),
        sortable: true
    },
    {
        headerName: 'Forfall',
        field: 'dueDays',
        headerClass: 'header-right',
        cellRenderer: DueDaysCellRenderer,
        cellRendererParams: ({data}: ICellRendererParams) => {
            return {
                display: data.dueDays,
                balance: data.balance
            }
        },
        resizable: false,
        width: 160,
        sortable: true
    },
    {
        headerName: 'Betalingsoppfølging',
        field: 'status',
        cellStyle: {'text-align': 'right'},
        valueGetter: (params: ValueGetterParams) => {
            return params.data.debtCollectionStatus
        },
        cellRenderer: InvoiceDebtCollectionCell,
        /*cellRendererParams: {
            openModal: openModal
        },*/
        headerClass: 'header-right',
        maxWidth: 300,
        minWidth: 100,
        flex: 1,
        hide: !ShowDebtCollection.isEnabled(),
        sortable: true
    }
]

const gridOptions: GridOptions = {
    rowHeight: 45,
    defaultColDef: {
        ...globalDefaultColDef,
        headerName: '',
        resizable: true,
        sortable: false,
        filter: false
    },
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    getRowId: (node: { data: { uuid: string } }) => {
        return node.data.uuid
    },
    context: {
        panelId,
        panelPathSegment
    }
}

const fetchInvoices = async () => {
    gridApi.value?.setGridOption('loading', true)
    allData.value = await getInvoiceBalancesWithDueDate()
    if (allData.value?.length === 0) {
        gridApi.value?.showNoRowsOverlay()
    } else {
        selectedInvoice.value = allData.value[0]
    }
    gridApi.value?.setGridOption('loading', false)
}

const clear = () => {
    allData.value = []
}

onMounted(() => {
    fetchInvoices()

    onEvent(TRANSACTION_CREATED, fetchInvoices)
    onEvent(TRANSACTION_UPDATED, fetchInvoices)
    onEvent(DELETE_TRANSACTION, fetchInvoices)
    onEvent(ORG_SWITCHED, () => {
        clear()
        fetchInvoices()
    })
})

watch(filteredInvoices, () => {
    const invoices = filteredInvoices.value || []
    if (invoices.length === 0) {
        selectedInvoice.value = undefined
    } else if (!selectedInvoice.value) {
        selectedInvoice.value = invoices[0]
    }
    gridApi.value?.refreshCells({ force: true })
})

onUnmounted(cleanUp)

</script>

<style scoped>

</style>